/* eslint-disable no-nested-ternary */
import IndicatorCategoryDot from "components/financials/IndicatorCategoryDot";
import IndicatorCategoryTag from "components/financials/IndicatorCategoryTag";
import isRangeSelectionSupported from "components/financials/modern/selection/isRangeSelectionSupported";
import SelectedIndicatorRangePeriod from "components/financials/modern/selection/SelectedIndicatorRangePeriod";
import SelectedIndicatorTrendIcon from "components/financials/modern/selection/SelectedIndicatorTrendIcon";
import SelectionFinancialRangeValues from "components/financials/modern/selection/SelectionFinancialRangeValues";
import SelectionHighLowAverageRangeValues from "components/financials/modern/selection/SelectionHighLowAverageRangeValues";
import SelectionUniqueRangeValues from "components/financials/modern/selection/SelectionUniqueRangeValues";
import type { IndicatorSelection, SheetIndicator } from "components/financials/modern/types";
import classNames from "components/ui/classNames";
import { Popover, PopoverContent } from "components/ui/Popover";
import stringHashCode from "lib/utils/stringHashCode";
import useTheme from "components/ui/useTheme";
import type { ReactNode } from "react";
import { useLayoutEffect, useState } from "react";
import { useIntl } from "react-intl";
import tinycolor from "tinycolor2";
import { IIndicatorKind } from "types/graphqlTypes";
import { useDebounceValue } from "usehooks-ts";

interface SelectedIndicatorRangePopoverProps {
  isOpen: boolean;
  indicator: SheetIndicator | null;
  selection: IndicatorSelection[];
  children: ReactNode;
  zIndex?: number;
}

export default function SelectedIndicatorRangePopover({
  isOpen,
  indicator,
  selection,
  children,
  zIndex,
}: SelectedIndicatorRangePopoverProps) {
  const intl = useIntl();
  const localeLanguageCode = intl.locale === "pt-BR" ? "pt" : "en";
  const documentation =
    indicator?.documentation.find(({ languageCode }) => languageCode === localeLanguageCode) ||
    indicator?.preferredDocumentation;

  // Gabriel Schmitt's: usar um debounce para guardar o estado da seleção ENQUANTO o usuário está selecionando. Depois
  // disso começar a contar até 600ms para CONFIRMAR a seleção, e aí mostrar o popup. Caso tal mecânica não seja feita
  // o popover ficará dando flick de acordo com o que o usuário seleciona, porque o React não consegue fazer rerender
  // de tudo na mesma velocidade que a interação do mouse acontece.
  const selectionHash = stringHashCode(selection.map((node) => node.reportDate.toISOString()).join(""));
  const [debouncedSelectedCount] = useDebounceValue(selection.length, 600);
  const [hasSelection, setHasSelection] = useState(false);
  const theme = useTheme();
  const isDarkTheme = theme === "dark";

  // Caso uma nova seleção aconteça, remover o que está selecionado atualmente, ou seja "matando" o popover antigo.
  useLayoutEffect(() => {
    setHasSelection(false);
  }, [selectionHash]);
  useLayoutEffect(() => {
    setHasSelection(debouncedSelectedCount > 0 && selection.length === debouncedSelectedCount);
  }, [debouncedSelectedCount, selection.length]);

  return (
    <Popover open={isOpen && hasSelection && isRangeSelectionSupported(indicator)}>
      {children}

      {indicator && documentation && selection.length >= 2 && (
        <PopoverContent
          style={{ zIndex }}
          sideOffset={24}
          side="right"
          align="center"
          className={classNames(
            "space-y-4 rounded-3xl border-slate-300 p-4 shadow-md",
            "dark:border-slate-950 dark:bg-slate-950",
            {
              "z-50": !zIndex,
              "border-slate-950 bg-slate-950": isDarkTheme,
              dark: isDarkTheme,
            },
          )}
        >
          <header className="flex justify-between space-x-12">
            <div className="space-y-0.5">
              <div className="flex items-center space-x-2">
                <IndicatorCategoryDot indicator={indicator} />
                <h1
                  className={classNames("text-base font-bold")}
                  style={{
                    color: isDarkTheme
                      ? tinycolor(indicator.columnTheme.backgroundColor).darken(20).toRgbString()
                      : indicator.columnTheme.textColor,
                  }}
                >
                  {documentation.abbreviation}
                </h1>
              </div>
              <p className="line-clamp-2 max-w-56 text-xs font-medium italic tracking-wider text-slate-500 dark:text-slate-400">
                {documentation.description}
              </p>
            </div>
            <div>
              <SelectedIndicatorTrendIcon selection={selection} />
            </div>
          </header>

          {(indicator.kind === IIndicatorKind.Financial || indicator.kind === IIndicatorKind.Currency) && (
            <SelectionFinancialRangeValues indicator={indicator} selection={selection} />
          )}
          {indicator.kind === IIndicatorKind.Percent && (
            <SelectionHighLowAverageRangeValues indicator={indicator} selection={selection} />
          )}
          {(indicator.kind === IIndicatorKind.Metric || indicator.kind === IIndicatorKind.Ratio) && (
            <SelectionFinancialRangeValues indicator={indicator} selection={selection} />
          )}
          {indicator.kind === IIndicatorKind.Text && <SelectionUniqueRangeValues selection={selection} />}
          <div className="flex items-center justify-between">
            <IndicatorCategoryTag indicator={indicator} />
            <span aria-hidden={true} className="w-6" />
            <SelectedIndicatorRangePeriod selection={selection} />
          </div>
        </PopoverContent>
      )}
    </Popover>
  );
}
